import {useSearchParams} from "react-router-dom";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "../../../basic/tabs";
import {Building2, GraduationCap, Shield, User} from "lucide-react";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {useAuth} from "../../../../hooks/useAuth";
import {useGetPortalTeamQuery} from "../../../../store/generated/api.ts";
import {AccountSettings} from "./AccountSettings";
import {EducationalSettings} from "./EducationalSettings";
import {TeamSettings} from "../../../shared/TeamSettings";
import {SecuritySettings} from "./SecuritySettings";

export function SettingsView() {
    const {t} = useTranslationWithKeys();
    const {user} = useAuth();
    const hasAdminRole = user?.roles?.some(role => ['ADMIN', 'PLATFORM_ADMIN'].includes(role));
    const {data: teamData} = useGetPortalTeamQuery({page: 0, size: 10});
    const isUniversity = teamData?.team?.isUniversity === true;
    const [searchParams, setSearchParams] = useSearchParams();

    const availableTabs = [
        "account",
        ...(hasAdminRole ? ["team"] : []),
        ...(hasAdminRole && isUniversity ? ["educational"] : []),
        "security",
    ];
    const requestedTab = searchParams.get("tab");
    const activeTab = requestedTab && availableTabs.includes(requestedTab) ? requestedTab : "account";

    const handleTabChange = (tab: string) => {
        setSearchParams(prev => {
            prev.set("tab", tab);
            return prev;
        }, {replace: true});
    };

    return (
        <div className="space-y-6">
            <div>
                <h1>{t('settings.title')}</h1>
                <p className="text-muted-foreground mt-1">{t('settings.subtitle')}</p>
            </div>

            <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
                <TabsList>
                    <TabsTrigger value="account">
                        <User className="h-4 w-4 mr-2"/>
                        {t('settings.account')}
                    </TabsTrigger>
                    {hasAdminRole && (
                        <TabsTrigger value="team">
                            <Building2 className="h-4 w-4 mr-2"/>
                            {t('settings.team')}
                        </TabsTrigger>
                    )}
                    {hasAdminRole && isUniversity && (
                        <TabsTrigger value="educational">
                            <GraduationCap className="h-4 w-4 mr-2"/>
                            {t('settings.educational.title')}
                        </TabsTrigger>
                    )}
                    <TabsTrigger value="security">
                        <Shield className="h-4 w-4 mr-2"/>
                        {t('settings.security')}
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="account">
                    <AccountSettings/>
                </TabsContent>

                {hasAdminRole && (
                    <TabsContent value="team">
                        <TeamSettings phoneNumberRequired={false}/>
                    </TabsContent>
                )}

                {hasAdminRole && isUniversity && (
                    <TabsContent value="educational">
                        <EducationalSettings/>
                    </TabsContent>
                )}

                <TabsContent value="security">
                    <SecuritySettings/>
                </TabsContent>
            </Tabs>
        </div>
    );
}